import cn from 'classnames'
import AlertBanner from 'ext-ui/components/AlertBanner/AlertBanner'
import Pill from 'ext-ui/components/Pill/Pill'
import NotificationsNewIcon from 'ext-ui/svg/icon/NotificationsNewIcon'
import React, { ComponentProps, ReactElement } from 'react'
import PricingEnterprisePNG from 'src/assets/images/pricing-enterprise.png'
import PricingProPNG from 'src/assets/images/pricing-pro.png'
import PricingStarterPNG from 'src/assets/images/pricing-starter.png'
import { useTranslate } from 'src/localization/Trns'
import { BillingCycle, SaasTierType } from 'src/models/organizations.models'

import style from './SaasPlanSummary.style'

export type SaasPlanSummaryProps = {
  className?: string
  tier: SaasTierType
  billingCycle?: BillingCycle
} & ComponentProps<'div'>

type TierCopy = {
  iconSrc: string
  label: string
  pillCopy?: string
  alertCopy?: string
}

function getTierCopy(
  tier: SaasTierType,
  translate: ReturnType<typeof useTranslate>,
  billingCycle?: BillingCycle,
): TierCopy {
  switch (tier) {
    case 'STARTER':
      return {
        iconSrc: PricingStarterPNG,
        label: translate('Starter Plan Selected'),
        pillCopy: translate('$0 per user/ month'),
      }
    case 'PRO':
      return {
        iconSrc: PricingProPNG,
        label: translate('Pro Plan Unlocked') + ' 🎉',
        // Annual Pro plans are not eligible for the 30-day free trial.
        pillCopy:
          billingCycle === BillingCycle.YEARLY
            ? undefined
            : translate('with 30 day free trial'),
      }
    case 'ENTERPRISE':
      return {
        iconSrc: PricingEnterprisePNG,
        label: translate('Enterprise Plan Selected'),
        pillCopy: translate('Custom pricing'),
        alertCopy: translate(
          'Our customer support team will reach out post card enrollment.',
        ),
      }
  }
}

function SaasPlanSummary(props: SaasPlanSummaryProps): ReactElement {
  const { className = '', tier, billingCycle, ...rest } = props
  const translate = useTranslate()
  const { iconSrc, label, pillCopy, alertCopy } = getTierCopy(
    tier,
    translate,
    billingCycle,
  )

  return (
    <div
      className={cn('SaasPlanSummary', className)}
      css={style}
      data-testid="saas-plan-summary"
      {...rest}
    >
      <div className="summary-row">
        <div className="summary-left">
          <img src={iconSrc} alt="" className="plan-icon" />
          <span className="plan-label">{label}</span>
        </div>
        {pillCopy && <Pill color="saas">{pillCopy}</Pill>}
      </div>
      {alertCopy && (
        <AlertBanner
          variant="soft"
          icon={<NotificationsNewIcon aria-hidden="true" />}
          title={alertCopy}
          data-testid="saas-plan-summary-alert"
        />
      )}
    </div>
  )
}

export default SaasPlanSummary
